import React, { useCallback, useContext, useMemo, useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import type { RootState } from "state/rootReducer";
import {
  Box,
  useColorModeValue,
  Stack,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
  Tag,
  Center,
  Button,
  Menu,
  MenuButton,
  Portal,
  MenuList,
  Text,
} from "@chakra-ui/react";
import { useCollectionKey, useContents, useLatestCollectionWorkflowId, useProjectParams, useTileProps, useUserProfile } from "hooks";
import { useDealFinderTickers } from "hooks/useDealFinderTickers";
import type { DealFinderTicker } from "api/tickers/models/DealFinderTicker";
import { BiDotsVertical, BiShareAlt } from "react-icons/bi";
import { SmallActionButton } from "screens/content/contentCanvas/cell/SmallActionButton";
import { ProjectHeaderImage } from "./components/ProjectHeaderImage";
import { CollectionsFilterContext } from "../../collections";
import { useNavigate } from "react-router-dom";
import { useConversationContext } from "screens/thread/ConversationContext";
import { WorkflowProgressBar } from "screens/landing/components/WorkflowProgressBar";
import { useCopyValue } from "hooks/useCopies";
import { useWorkflowCompletionDateOrNow, useWorkflowKey } from "hooks/useWorkflows";
import { formatDistanceToNow } from "date-fns";
import { ProjectDeleteButton } from "screens/collection/views/ProjectActions/ProjectDeleteButton";
import { MoveProjectModal } from "screens/collection/views/ProjectActions/MoveProjectModal";
import { v4 as uuid } from "uuid";
import { ProjectMoveButton } from "screens/collection/views/ProjectActions/ProjectMoveButton";
import { useProjectMove } from "hooks/useProjectMove";
import { WorkflowProgressStepperContainer } from "screens/landing/components/WorkflowProgressStepper";
import { TextOverflowTooltip } from "screens/landing/components/TextOverflowTooltip";
import { ShowMoreContainer } from "screens/common/components/ShowMoreContainer";
import { MarkdownViewer } from "screens/markdown/MarkdownViewer";

interface ProjectLandingSmallTileProps {
  collectionId: string;
  logoUrl?: string;
}

const ProjectLandingCompanyTile: React.FC<ProjectLandingSmallTileProps> = ({ collectionId, logoUrl }) => {
  const { projectFilter } = useProjectParams();
  const tileBgColor = useColorModeValue("white", "#161B25");
  const tileBorderColor = useColorModeValue("gray.300", "gray.700");
  const tagBgColor = useColorModeValue("orange.200", "gray.800");
  const tagBorderColor = useColorModeValue("gray.300", "gray.700");
  const tagColor = useColorModeValue("gray.600", "gray.500");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("primary.darkGray", "gray.300");
  const defaultIconColor = useColorModeValue("gray.500", "gray.400");
  const commonTileProps = useTileProps();
  const isMobile = useBreakpointValue({ base: true, md: false, lg: false }, { fallback: "md", ssr: false });
  const navigate = useNavigate();

  // Deal Finder Tickers hook and state
  const { getDealFinderTickers } = useDealFinderTickers();
  const [tickerData, setTickerData] = useState<DealFinderTicker | null>(null);

  const collection = useSelector(
    (state: RootState) => (collectionId ? state.collection.collections[collectionId] : undefined),
    shallowEqual
  );

  const projectName = collection?.name;
  const projectMetadataIds = collection?.metadataIds;
  const collectionType = collection?.collectionType;
  const projectGroupId = collection?.projectGroupId;
  const shareDetails = collection?.shareDetails;
  const ticker = collection?.ticker;
  const exchange = collection?.stockExchange;
  const collectionConversationId = collection?.conversationId;
  const portfolioCollectionType = useCollectionKey(projectFilter, "collectionType");
  const isInvestorRelationsPortfolio = useMemo(() => {
    return portfolioCollectionType === "investor_relations_portfolio";
  }, [portfolioCollectionType]);
  const showProjectRerun = !isInvestorRelationsPortfolio;

  const companyLogoData = useContents(projectMetadataIds, {
    refreshFromNetwork: true,
    filterByTags: ["company_logo"],
  });

  const currentWorkflowId = useLatestCollectionWorkflowId(collectionId);
  const currentWorkflowCompletionDate = useWorkflowCompletionDateOrNow(currentWorkflowId);
  const isPlaceholderTile = collectionId === "placeholder";

  // Fetch ticker data when ticker and exchange are available
  useEffect(() => {
    const fetchTickerData = async () => {
      if (ticker && exchange) {
        try {
          const data = await getDealFinderTickers({
            symbolWithExchange: `${exchange}:${ticker}`,
          });
          setTickerData(data.length > 0 ? data[0] : null);
        } catch (error) {
          console.error("Error fetching ticker data:", error);
          setTickerData(null);
        }
      }
    };

    fetchTickerData();
  }, [ticker, exchange, getDealFinderTickers]);

  const isSharedRead = shareDetails && shareDetails.accessMode === "read";
  const { setSelectedProjectId, deletedCollectionIds } = useContext(CollectionsFilterContext);
  const { onDeleteProjectModalOpen } = useConversationContext();

  // Check if this collection has been deleted
  const isDeleted = useMemo(() => {
    return deletedCollectionIds?.includes(collectionId);
  }, [deletedCollectionIds, collectionId]);
  const { onOpen: onOpenMoveProject, onClose: onCloseMoveProject, isOpen: isOpenMoveProject } = useDisclosure();

  // Use the project move hook for remove functionality
  const { removeFromPortfolio, isInPortfolio } = useProjectMove({
    collectionId,
    conversationId: collectionConversationId || uuid(),
  });
  const placeholderTileText = useCopyValue("copy_project_placeholder_message_company");
  const workflowStatus = useWorkflowKey(currentWorkflowId, "status");

  const handleActionClick = useCallback((actionType: string, collectionId: string) => {
    // Don't close the modal on action clicks
    return false;
  }, []);

  const getPath = () => {
    return navigate(`/investor-relations-dashboard/${projectFilter}/${collectionId}`);
  };

  const { id: currentUserId } = useUserProfile();
  const isSharedOwner = useMemo(() => {
    if (!shareDetails) {
      return true;
    }
    return shareDetails?.ownerId === currentUserId;
  }, [currentUserId, shareDetails]);

  // Don't render if the collection has been deleted
  if (isDeleted) {
    return null;
  }

  return (
    <>
      <Box
        {...commonTileProps}
        bgColor={tileBgColor}
        p="0"
        onClick={() => {
          if (!isPlaceholderTile) {
            getPath();
          }
        }}
        onMouseEnter={() => setSelectedProjectId(collectionId)}
        overflow="visible"
        textAlign="left"
        className={`${
          isPlaceholderTile ? "ch-project-landing-placeholder-tile" : `ch-project-landing-default-${isSharedRead ? "shared" : "tile"}`
        }`}
        cursor="pointer">
        <Stack spacing={"0"} direction="column" h="full">
          <Stack spacing={isMobile ? "0.5rem" : "1rem"} direction="column" h="full" p={isMobile ? "0.5rem" : "1rem"}>
            <Stack direction="row" justifyContent={"space-between"} width="100%" alignItems="center">
              {projectName && (
                <Stack direction={"row"} spacing={"0.5rem"} alignItems="center" maxHeight={"20px"}>
                  {currentWorkflowId && (
                    <Stack direction={"row"} spacing={"0.5rem"} alignItems="center">
                      <Center>
                        <WorkflowProgressBar
                          workflowId={currentWorkflowId}
                          size="compact"
                          lastUpdated={formatDistanceToNow(new Date(currentWorkflowCompletionDate), {
                            addSuffix: true,
                            includeSeconds: false,
                          })}
                          progressIndicatorStyle="circle"
                          conversationId={collectionConversationId}
                        />
                      </Center>
                    </Stack>
                  )}
                  <TextOverflowTooltip
                    label={projectName?.toLowerCase() === "new project" ? "" : projectName || ""}
                    color={textColor}
                    noOfLines={1}
                    className="ch-project-landing-title"
                  />
                  <Text fontSize="xs" color={textColor}>
                    ({exchange}:{ticker})
                  </Text>
                </Stack>
              )}

              <Tag
                className="ch-project-landing-ir-dashboard-status-tag"
                paddingInline={"5px"}
                zIndex={2}
                variant="subtle"
                borderWidth="1px"
                borderColor={tagBorderColor}
                bgColor={tagBgColor}
                color={tagColor}
                cursor={"pointer"}
                onClick={(evt) => {
                  evt.stopPropagation();
                }}>
                PENDING
              </Tag>
            </Stack>
            <Stack direction={"row"} spacing={"1rem"} alignItems="top" justifyContent={"space-between"}>
              <Stack minWidth={isMobile ? "7rem" : "10rem"} overflow={"hidden"} spacing={"1.5rem"}>
                <ProjectHeaderImage
                  maxHeight={"2rem"}
                  width="unset"
                  collectionId={collectionId}
                  projectName={projectName?.toLowerCase() === "new project" ? "" : projectName || ""}
                  projectType={collectionType}
                  projectGroupId={projectGroupId}
                  logoOnly
                  logoUrl={logoUrl || tickerData?.companyLogoUrl || undefined}
                  mediaId={companyLogoData?.[0]?.id}
                />
              </Stack>
              <Box color={textColor} width="100%">
                <ShowMoreContainer minHeight="8.5rem" maxHeight="5.5rem">
                  <MarkdownViewer content={placeholderTileText} noBoldHeadings />
                </ShowMoreContainer>
              </Box>
            </Stack>
            {(currentWorkflowId && isPlaceholderTile) ||
              (workflowStatus === "in_progress" && (
                <Box
                  zIndex={1}
                  position={"absolute"}
                  width="100%"
                  left="0"
                  bottom="2.7rem"
                  borderColor={tileBorderColor}
                  borderTopWidth="1px"
                  p="10px"
                  bgColor={tileBgColor}>
                  <WorkflowProgressStepperContainer collectionId={collectionId} hideProgressBar={false} />
                </Box>
              ))}
          </Stack>
          <Stack
            direction="row"
            width="100%"
            height="2rem"
            justify="space-between"
            spacing={isMobile ? ".5rem" : "1rem"}
            px={isMobile ? ".5rem" : "1rem"}
            py={isMobile ? ".2rem" : ".5rem"}
            borderTop="1px solid"
            borderColor={borderColor}>
            <Stack direction="row" justify="space-between" spacing={"1rem"}>
              <Tooltip label="Coming Soon" placement="top" aria-label="Like">
                <Stack>
                  <SmallActionButton
                    // isDisabled={!isSharedOwner || isPlaceholderTile || workflowStatus === "in_progress"}
                    isDisabled
                    iconSize="1rem"
                    tooltip="Share"
                    iconTypeName={BiShareAlt}
                    onClick={() => handleActionClick("share", collectionId)}
                  />
                </Stack>
              </Tooltip>
            </Stack>
            <Stack direction="row" justify="space-between" spacing={"1rem"}>
              <Menu isLazy placement="bottom-end">
                <MenuButton
                  height="0.9rem"
                  width="0.5rem"
                  minWidth={"unset"}
                  paddingInline={0}
                  border="none"
                  _hover={{
                    color: "primary.default",
                  }}
                  _active={{}}
                  color={defaultIconColor}
                  bgColor={"transparent"}
                  className="ch-content-options"
                  as={Button}
                  aria-label="Options"
                  rightIcon={<BiDotsVertical color={defaultIconColor} />}
                  onClick={(event) => event.stopPropagation()}></MenuButton>
                <Portal>
                  <MenuList minWidth={"unset"} zIndex={10} boxShadow={"md"}>
                    <ProjectDeleteButton
                      isDisabled={!isSharedOwner || isPlaceholderTile}
                      label={"Delete Project"}
                      onOpenConfirmation={onDeleteProjectModalOpen}
                      projectId={collectionId}
                      projectType={"project"}
                      isMenuItem
                    />
                    {showProjectRerun && (
                      <ProjectMoveButton
                        isDisabled={!isSharedOwner || isPlaceholderTile || workflowStatus === "in_progress"}
                        isMenuItem
                        onClickMove={onOpenMoveProject}
                        onClickRemove={isInPortfolio ? removeFromPortfolio : undefined}
                      />
                    )}
                  </MenuList>
                </Portal>
              </Menu>
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <MoveProjectModal
        isOpen={isOpenMoveProject}
        onClose={() => {
          onCloseMoveProject();
        }}
        collectionId={collectionId}
        conversationId={collectionConversationId || uuid()}
      />
    </>
  );
};

export default ProjectLandingCompanyTile;
